// Google Maps place type utilities
// Helper functions for categorizing and formatting place data

// Helper to extract the primary place type from Google Maps types array
export const getPrimaryPlaceType = (types) => {
  if (!types || !Array.isArray(types) || types.length === 0) {
    return null;
  }

  // Check for geographic types first (neighborhoods, cities, etc.)
  const geographicTypes = ['neighborhood', 'locality', 'sublocality', 'administrative_area_level_1',
                          'administrative_area_level_2', 'country', 'postal_code', 'natural_feature',
                          'colloquial_area', 'premise', 'subpremise'];
  const geoType = types.find(type => geographicTypes.includes(type));
  if (geoType) {
    return geoType;
  }

  // Filter out generic types like 'point_of_interest' and 'establishment'
  const filteredTypes = types.filter(
    type => type !== 'point_of_interest' && type !== 'establishment'
  );

  // Return the first specific type, or the first type if no specific ones found
  return filteredTypes.length > 0 ? filteredTypes[0] : types[0];
};

// Helper to map Google place type to high-level categories
// Supports both legacy Places API types and Places API (New) types
export const mapPlaceTypeToCategory = (placeType) => {
  if (!placeType) {
    return 'Activities';
  }

  const categoryMap = {
    // General Places (Geographic areas)
    'neighborhood': 'General Places',
    'locality': 'General Places',
    'sublocality': 'General Places',
    'administrative_area_level_1': 'General Places',
    'administrative_area_level_2': 'General Places',
    'country': 'General Places',
    'postal_code': 'General Places',
    'natural_feature': 'General Places',
    'colloquial_area': 'General Places',
    'premise': 'General Places',
    'subpremise': 'General Places',

    // Food & Drinks - Legacy types
    'restaurant': 'Food & Drinks',
    'cafe': 'Food & Drinks',
    'bar': 'Food & Drinks',
    'bakery': 'Food & Drinks',
    'meal_takeaway': 'Food & Drinks',
    'meal_delivery': 'Food & Drinks',
    'food': 'Food & Drinks',
    'night_club': 'Food & Drinks',
    'liquor_store': 'Food & Drinks',
    // Food & Drinks - New API cuisine-specific restaurants
    'american_restaurant': 'Food & Drinks',
    'asian_restaurant': 'Food & Drinks',
    'barbecue_restaurant': 'Food & Drinks',
    'brazilian_restaurant': 'Food & Drinks',
    'breakfast_restaurant': 'Food & Drinks',
    'brunch_restaurant': 'Food & Drinks',
    'buffet_restaurant': 'Food & Drinks',
    'chinese_restaurant': 'Food & Drinks',
    'coffee_shop': 'Food & Drinks',
    'diner': 'Food & Drinks',
    'fast_food_restaurant': 'Food & Drinks',
    'fine_dining_restaurant': 'Food & Drinks',
    'french_restaurant': 'Food & Drinks',
    'greek_restaurant': 'Food & Drinks',
    'hamburger_restaurant': 'Food & Drinks',
    'indian_restaurant': 'Food & Drinks',
    'indonesian_restaurant': 'Food & Drinks',
    'italian_restaurant': 'Food & Drinks',
    'japanese_restaurant': 'Food & Drinks',
    'korean_restaurant': 'Food & Drinks',
    'lebanese_restaurant': 'Food & Drinks',
    'mediterranean_restaurant': 'Food & Drinks',
    'mexican_restaurant': 'Food & Drinks',
    'middle_eastern_restaurant': 'Food & Drinks',
    'pizza_restaurant': 'Food & Drinks',
    'ramen_restaurant': 'Food & Drinks',
    'sandwich_shop': 'Food & Drinks',
    'seafood_restaurant': 'Food & Drinks',
    'spanish_restaurant': 'Food & Drinks',
    'steak_house': 'Food & Drinks',
    'sushi_restaurant': 'Food & Drinks',
    'thai_restaurant': 'Food & Drinks',
    'turkish_restaurant': 'Food & Drinks',
    'vegan_restaurant': 'Food & Drinks',
    'vegetarian_restaurant': 'Food & Drinks',
    'vietnamese_restaurant': 'Food & Drinks',
    // Food & Drinks - New API bars/drinks
    'pub': 'Food & Drinks',
    'wine_bar': 'Food & Drinks',
    'bar_and_grill': 'Food & Drinks',
    'cocktail_bar': 'Food & Drinks',
    'tea_house': 'Food & Drinks',
    'juice_shop': 'Food & Drinks',
    // Food & Drinks - New API specialty food
    'acai_shop': 'Food & Drinks',
    'bagel_shop': 'Food & Drinks',
    'candy_store': 'Food & Drinks',
    'chocolate_shop': 'Food & Drinks',
    'deli': 'Food & Drinks',
    'donut_shop': 'Food & Drinks',
    'food_court': 'Food & Drinks',
    'ice_cream_shop': 'Food & Drinks',

    // Shopping - Legacy types
    'store': 'Shopping',
    'shopping_mall': 'Shopping',
    'clothing_store': 'Shopping',
    'convenience_store': 'Shopping',
    'department_store': 'Shopping',
    'electronics_store': 'Shopping',
    'furniture_store': 'Shopping',
    'hardware_store': 'Shopping',
    'home_goods_store': 'Shopping',
    'jewelry_store': 'Shopping',
    'shoe_store': 'Shopping',
    'book_store': 'Shopping',
    'florist': 'Shopping',
    'pet_store': 'Shopping',
    'bicycle_store': 'Shopping',
    'supermarket': 'Shopping',
    'grocery_or_supermarket': 'Shopping',
    // Shopping - New API types
    'asian_grocery_store': 'Shopping',
    'auto_parts_store': 'Shopping',
    'butcher_shop': 'Shopping',
    'cell_phone_store': 'Shopping',
    'discount_store': 'Shopping',
    'food_store': 'Shopping',
    'gift_shop': 'Shopping',
    'grocery_store': 'Shopping',
    'market': 'Shopping',
    'sporting_goods_store': 'Shopping',
    'warehouse_store': 'Shopping',
    'wholesaler': 'Shopping',

    // Museums/Culture - Legacy types
    'museum': 'Museums/Culture',
    'art_gallery': 'Museums/Culture',
    'library': 'Museums/Culture',
    'church': 'Museums/Culture',
    'hindu_temple': 'Museums/Culture',
    'mosque': 'Museums/Culture',
    'synagogue': 'Museums/Culture',
    'tourist_attraction': 'Museums/Culture',
    'aquarium': 'Museums/Culture',
    'zoo': 'Museums/Culture',
    // Museums/Culture - New API types
    'art_studio': 'Museums/Culture',
    'auditorium': 'Museums/Culture',
    'cultural_landmark': 'Museums/Culture',
    'historical_landmark': 'Museums/Culture',
    'historical_place': 'Museums/Culture',
    'monument': 'Museums/Culture',
    'performing_arts_theater': 'Museums/Culture',
    'sculpture': 'Museums/Culture',

    // Activities - Legacy types
    'amusement_park': 'Activities',
    'bowling_alley': 'Activities',
    'casino': 'Activities',
    'movie_theater': 'Activities',
    'stadium': 'Activities',
    'gym': 'Activities',
    'spa': 'Activities',
    'beauty_salon': 'Activities',
    'hair_care': 'Activities',
    // Activities - New API entertainment
    'adventure_sports_center': 'Activities',
    'amphitheatre': 'Activities',
    'amusement_center': 'Activities',
    'comedy_club': 'Activities',
    'concert_hall': 'Activities',
    'convention_center': 'Activities',
    'dance_hall': 'Activities',
    'event_venue': 'Activities',
    'ferris_wheel': 'Activities',
    'karaoke': 'Activities',
    'movie_rental': 'Activities',
    'observation_deck': 'Activities',
    'opera_house': 'Activities',
    'philharmonic_hall': 'Activities',
    'planetarium': 'Activities',
    'roller_coaster': 'Activities',
    'video_arcade': 'Activities',
    'water_park': 'Activities',
    'wedding_venue': 'Activities',
    // Activities - New API sports/fitness
    'arena': 'Activities',
    'athletic_field': 'Activities',
    'fitness_center': 'Activities',
    'golf_course': 'Activities',
    'ice_skating_rink': 'Activities',
    'playground': 'Activities',
    'ski_resort': 'Activities',
    'sports_activity_location': 'Activities',
    'sports_club': 'Activities',
    'sports_coaching': 'Activities',
    'sports_complex': 'Activities',
    'swimming_pool': 'Activities',
    // Activities - New API wellness
    'barber_shop': 'Activities',
    'massage': 'Activities',
    'sauna': 'Activities',
    'skin_care_clinic': 'Activities',
    'tanning_studio': 'Activities',
    'wellness_center': 'Activities',
    'yoga_studio': 'Activities',

    // Hotels - Legacy types
    'lodging': 'Hotels',
    'hotel': 'Hotels',
    // Hotels - New API types
    'bed_and_breakfast': 'Hotels',
    'budget_japanese_inn': 'Hotels',
    'campground': 'Hotels',
    'camping_cabin': 'Hotels',
    'cottage': 'Hotels',
    'extended_stay_hotel': 'Hotels',
    'farmstay': 'Hotels',
    'guest_house': 'Hotels',
    'hostel': 'Hotels',
    'inn': 'Hotels',
    'japanese_inn': 'Hotels',
    'mobile_home_park': 'Hotels',
    'motel': 'Hotels',
    'private_guest_room': 'Hotels',
    'resort_hotel': 'Hotels',
    'rv_park': 'Hotels',

    // Parks/Outdoors - Legacy types
    'park': 'Parks/Outdoors',
    'national_park': 'Parks/Outdoors',
    'state_park': 'Parks/Outdoors',
    'hiking_area': 'Parks/Outdoors',
    'trail': 'Parks/Outdoors',
    // Parks/Outdoors - New API types
    'barbecue_area': 'Parks/Outdoors',
    'beach': 'Parks/Outdoors',
    'botanical_garden': 'Parks/Outdoors',
    'cycling_park': 'Parks/Outdoors',
    'dog_park': 'Parks/Outdoors',
    'garden': 'Parks/Outdoors',
    'marina': 'Parks/Outdoors',
    'off_roading_area': 'Parks/Outdoors',
    'picnic_ground': 'Parks/Outdoors',
    'skateboard_park': 'Parks/Outdoors',
    'visitor_center': 'Parks/Outdoors',
    'wildlife_park': 'Parks/Outdoors',
    'wildlife_refuge': 'Parks/Outdoors',
  };

  // Check direct mapping first
  if (categoryMap[placeType]) {
    return categoryMap[placeType];
  }

  // Fallback patterns for types not explicitly mapped
  // Any type ending in _restaurant is Food & Drinks
  if (placeType.endsWith('_restaurant')) {
    return 'Food & Drinks';
  }
  // Any type ending in _store is Shopping
  if (placeType.endsWith('_store')) {
    return 'Shopping';
  }
  // Any type ending in _shop is likely Food or Shopping
  if (placeType.endsWith('_shop')) {
    return 'Shopping';
  }

  return 'Activities';
};

// Helper to get a human-readable subcategory from place type
// Returns a descriptive label like "Italian", "Mexican", "Coffee", etc.
export const getSubcategoryFromType = (placeType) => {
  if (!placeType) {
    return null;
  }

  const subcategoryMap = {
    // Cuisine-specific restaurants
    'american_restaurant': 'American',
    'asian_restaurant': 'Asian',
    'barbecue_restaurant': 'BBQ',
    'brazilian_restaurant': 'Brazilian',
    'breakfast_restaurant': 'Breakfast',
    'brunch_restaurant': 'Brunch',
    'buffet_restaurant': 'Buffet',
    'chinese_restaurant': 'Chinese',
    'diner': 'Diner',
    'fast_food_restaurant': 'Fast Food',
    'fine_dining_restaurant': 'Fine Dining',
    'french_restaurant': 'French',
    'greek_restaurant': 'Greek',
    'hamburger_restaurant': 'Burgers',
    'indian_restaurant': 'Indian',
    'indonesian_restaurant': 'Indonesian',
    'italian_restaurant': 'Italian',
    'japanese_restaurant': 'Japanese',
    'korean_restaurant': 'Korean',
    'lebanese_restaurant': 'Lebanese',
    'mediterranean_restaurant': 'Mediterranean',
    'mexican_restaurant': 'Mexican',
    'middle_eastern_restaurant': 'Middle Eastern',
    'pizza_restaurant': 'Pizza',
    'ramen_restaurant': 'Ramen',
    'sandwich_shop': 'Sandwiches',
    'seafood_restaurant': 'Seafood',
    'spanish_restaurant': 'Spanish',
    'steak_house': 'Steakhouse',
    'sushi_restaurant': 'Sushi',
    'thai_restaurant': 'Thai',
    'turkish_restaurant': 'Turkish',
    'vegan_restaurant': 'Vegan',
    'vegetarian_restaurant': 'Vegetarian',
    'vietnamese_restaurant': 'Vietnamese',
    // Bars/Drinks
    'bar': 'Bar',
    'pub': 'Pub',
    'wine_bar': 'Wine Bar',
    'bar_and_grill': 'Bar & Grill',
    'cocktail_bar': 'Cocktails',
    'night_club': 'Nightclub',
    // Cafes/Coffee
    'cafe': 'Cafe',
    'coffee_shop': 'Coffee',
    'tea_house': 'Tea',
    'juice_shop': 'Juice',
    // Specialty food
    'acai_shop': 'Acai',
    'bagel_shop': 'Bagels',
    'bakery': 'Bakery',
    'candy_store': 'Candy',
    'chocolate_shop': 'Chocolate',
    'deli': 'Deli',
    'donut_shop': 'Donuts',
    'ice_cream_shop': 'Ice Cream',
    'food_court': 'Food Court',
    // Shopping subcategories
    'asian_grocery_store': 'Asian Grocery',
    'grocery_store': 'Grocery',
    'supermarket': 'Supermarket',
    'butcher_shop': 'Butcher',
    'market': 'Market',
    'convenience_store': 'Convenience',
    'clothing_store': 'Clothing',
    'electronics_store': 'Electronics',
    'furniture_store': 'Furniture',
    'home_goods_store': 'Home Goods',
    'book_store': 'Books',
    'sporting_goods_store': 'Sports',
    'gift_shop': 'Gifts',
    // Culture subcategories
    'art_gallery': 'Art Gallery',
    'art_studio': 'Art Studio',
    'museum': 'Museum',
    'historical_landmark': 'Historical',
    'cultural_landmark': 'Cultural',
    'performing_arts_theater': 'Theater',
    'aquarium': 'Aquarium',
    'zoo': 'Zoo',
    // Hotels subcategories
    'hotel': 'Hotel',
    'motel': 'Motel',
    'hostel': 'Hostel',
    'resort_hotel': 'Resort',
    'bed_and_breakfast': 'B&B',
    'inn': 'Inn',
    'campground': 'Campground',
    // Parks/Outdoors subcategories
    'national_park': 'National Park',
    'state_park': 'State Park',
    'beach': 'Beach',
    'hiking_area': 'Hiking',
    'botanical_garden': 'Botanical',
    'dog_park': 'Dog Park',
    'marina': 'Marina',
    // Activities subcategories
    'gym': 'Gym',
    'fitness_center': 'Fitness',
    'yoga_studio': 'Yoga',
    'spa': 'Spa',
    'golf_course': 'Golf',
    'bowling_alley': 'Bowling',
    'movie_theater': 'Cinema',
    'concert_hall': 'Concert Hall',
    'comedy_club': 'Comedy',
    'karaoke': 'Karaoke',
    'video_arcade': 'Arcade',
    'amusement_park': 'Amusement Park',
    'water_park': 'Water Park',
    'ski_resort': 'Ski Resort',
    'casino': 'Casino',
  };

  return subcategoryMap[placeType] || null;
};

// Helper to format price level as dollar signs
export const formatPriceLevel = (priceLevel) => {
  if (priceLevel === null || priceLevel === undefined) {
    return null;
  }

  switch (priceLevel) {
    case 0:
      return 'Free';
    case 1:
      return '$';
    case 2:
      return '$$';
    case 3:
      return '$$$';
    case 4:
      return '$$$$';
    default:
      return null;
  }
};
